import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Trash2, AlertTriangle, RotateCcw } from "lucide-react";

export default function PurgeCnModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removeFromStudies, setRemoveFromStudies] = useState(true);
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    api.get("/admin/catalog/purges").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  };
  useEffect(loadHistory, []);

  const restore = async (p) => {
    if (!window.confirm(
      `Se repondrán ${p.catalog_count} CN en el catálogo` +
      (p.lines_count ? ` y ${p.lines_count} línea(s) en ${p.studies_count} estudio(s)` : "") +
      ". ¿Deshacer esta eliminación?"
    )) return;
    setBusy(true);
    const t = toast.loading("Restaurando…");
    try {
      const { data } = await api.post(`/admin/catalog/purges/${p.id}/restore`);
      toast.success(
        `${data.restored_catalog} CN repuestos · ${data.lines_restored} líneas en ${data.studies_updated} estudio(s)`,
        { id: t, duration: 9000 },
      );
      loadHistory();
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al restaurar", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setBusy(true);
    const t = toast.loading("Analizando fichero…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/catalog/purge/preview", fd);
      setPreview(data);
      toast.success(`${data.in_catalog} CN encontrados en el catálogo`, { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se ha podido leer el fichero", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!file || !preview) return;
    if (!window.confirm(
      `Se eliminarán ${preview.in_catalog} CN del catálogo` +
      (removeFromStudies ? ` y ${preview.study_lines} línea(s) en ${preview.studies_affected} estudio(s)` : "") +
      ". Se guarda copia de seguridad. ¿Continuar?"
    )) return;
    setBusy(true);
    const t = toast.loading("Eliminando…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("remove_from_studies", removeFromStudies ? "true" : "false");
      const { data } = await api.post("/admin/catalog/purge/apply", fd);
      toast.success(
        `${data.deleted} CN eliminados · ${data.lines_removed} líneas en ${data.studies_updated} estudio(s)`,
        { id: t, duration: 9000 },
      );
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar", { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 z-50 flex items-center justify-center p-4" data-testid="purge-cn-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-700">Nomenclátor</div>
            <div className="font-display text-lg font-bold text-slate-900">Eliminar CN del catálogo</div>
          </div>
          <button onClick={onClose} data-testid="purge-cn-close" className="text-slate-400 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Sube el Excel o CSV con los CN que ya no existen. Si el fichero tiene una hoja llamada
            <b> «Eliminar»</b>, sólo se usará esa hoja. Se guarda copia de seguridad antes de borrar.
          </p>

          <label
            data-testid="purge-cn-file"
            className="cursor-pointer flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md py-6 text-sm font-medium text-slate-600 hover:border-slate-900 hover:bg-slate-50"
          >
            <Upload className="h-4 w-4" />
            {file ? file.name : "Seleccionar Excel o CSV"}
            <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={pick} />
          </label>

          {preview && (
            <div className="space-y-3" data-testid="purge-cn-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["Hoja usada", preview.sheet],
                  ["CN en fichero", preview.cns_in_file],
                  ["En catálogo", preview.in_catalog],
                  ["No estaban", preview.not_in_catalog],
                ].map(([k, v]) => (
                  <div key={k} className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k}</div>
                    <div className="font-mono-num text-lg font-bold text-slate-900 truncate">{v}</div>
                  </div>
                ))}
              </div>

              {preview.studies_affected > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900 flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <b>{preview.studies_affected} estudio(s)</b> ya subidos contienen estos CN
                    ({preview.study_lines} líneas · {preview.study_units} uds). Si marcas la opción de abajo, esas
                    líneas se eliminarán también y los porcentajes de esos estudios se recalcularán.
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={removeFromStudies}
                  onChange={(e) => setRemoveFromStudies(e.target.checked)}
                  data-testid="purge-cn-remove-studies"
                />
                Eliminar también estas líneas de los estudios ya subidos
              </label>

              {preview.sample?.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Muestra de lo que se borrará
                  </div>
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-slate-100">
                      {preview.sample.map((it) => (
                        <tr key={it.cn}>
                          <td className="px-3 py-1.5 font-mono-num">{it.cn}</td>
                          <td className="px-3 py-1.5 max-w-[240px] truncate" title={it.name}>{it.name}</td>
                          <td className="px-3 py-1.5 text-slate-500 max-w-[160px] truncate">{it.laboratorio}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {history.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="purge-history">
              <div className="px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Eliminaciones anteriores · puedes deshacerlas
              </div>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-slate-100">
                  {history.map((p) => (
                    <tr key={p.id} data-testid={`purge-history-row-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900 truncate max-w-[220px]" title={p.filename}>
                          {p.filename || "(sin nombre)"}
                        </div>
                        <div className="text-slate-500">
                          {new Date(p.created_at).toLocaleString("es-ES")} · {p.admin_email}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono-num whitespace-nowrap">
                        {p.catalog_count} CN
                        {p.lines_count ? ` · ${p.lines_count} líneas` : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.restored_at ? (
                          <span className="text-emerald-700 font-semibold whitespace-nowrap">Restaurada</span>
                        ) : (
                          <button
                            onClick={() => restore(p)}
                            disabled={busy}
                            data-testid={`purge-restore-${p.id}`}
                            className="inline-flex items-center gap-1 border border-slate-300 rounded-md px-2 py-1 font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Deshacer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={apply}
            disabled={busy || !preview || preview.in_catalog === 0}
            data-testid="purge-cn-apply"
            className="inline-flex items-center gap-2 bg-rose-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-rose-800 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {busy ? "Procesando…" : "Eliminar del catálogo"}
          </button>
        </div>
      </div>
    </div>
  );
}
